import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Users, Zap, Check } from 'lucide-react';

export default function AdvertisePage() {
  const stats = [
    { label: 'Monthly Readers', value: '50K+' },
    { label: 'Newsletter Subscribers', value: '10K+' },
    { label: 'Social Reach', value: '100K+' },
    { label: 'Avg. Time on Site', value: '5 min' }
  ];

  const packages = [
    {
      name: 'Starter',
      price: '$500',
      period: '/month',
      description: 'Perfect for emerging brands testing the waters',
      features: [
        'Banner ad on homepage',
        'Featured in newsletter (1x/month)',
        'Social media mention',
        'Monthly analytics report'
      ],
      highlight: false
    },
    {
      name: 'Professional',
      price: '$1,200',
      period: '/month',
      description: 'Ideal for established brands seeking visibility',
      features: [
        'Everything in Starter',
        'Sponsored article feature',
        'Premium ad placement',
        'Newsletter feature (2x/month)',
        'Dedicated account manager',
        'Bi-weekly analytics'
      ],
      highlight: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'Tailored solutions for major campaigns',
      features: [
        'Everything in Professional',
        'Custom content series',
        'Video feature opportunities',
        'Exclusive partnerships',
        'Priority placement',
        'Quarterly strategy sessions'
      ],
      highlight: false
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Targeted Audience',
      description: 'Reach decision-makers in brands, marketing professionals, and influential creators.'
    },
    {
      icon: TrendingUp,
      title: 'High Engagement',
      description: 'Our readers spend 5+ minutes per visit and actively engage with sponsored content.'
    },
    {
      icon: Users,
      title: 'Premium Community',
      description: 'Connect with an audience that values quality, authenticity, and innovation.'
    },
    {
      icon: Zap,
      title: 'Proven Results',
      description: 'Partners see average CTR of 3.5% and strong brand awareness lift.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Advertise With Us · B&B Magazine</title>
        <meta name="description" content="Reach 50K+ engaged readers in the creator economy. Explore advertising opportunities with B&B Magazine." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com/advertise" />
      </Helmet>

      <HeaderNav />

      <main>
        {/* Hero Section */}
        <section className="w-full py-16 md:py-24 relative overflow-hidden">
          <div 
            aria-hidden="true" 
            className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(255,68,102,0.12),rgba(0,0,0,0)_70%)]"
          />
          
          <div className="container max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                Partner With Us
              </span>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
                Advertise With <br className="sm:hidden" />B&B Magazine
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-[65ch] mx-auto">
                Connect with 50,000+ engaged readers who are shaping the future of brands and the creator economy.
              </p>
              <div className="mt-8">
                <Button asChild size="lg" className="font-ui">
                  <Link to="/contact">Get Started</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full py-12 bg-[hsl(var(--surface-1))]">
          <div className="container max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="font-display text-3xl md:text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-muted-foreground font-ui">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="w-full py-16 md:py-20">
          <div className="container max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                Why Advertise With Us?
              </h2>
              <p className="text-muted-foreground max-w-[65ch] mx-auto">
                B&B Magazine offers unique access to an audience that drives the creator economy forward.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className="p-6 bg-[hsl(var(--surface-2))] border-border h-full hover:border-primary/30 transition-colors duration-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[hsl(var(--primary)/0.12)] border border-[hsl(var(--primary)/0.3)] flex items-center justify-center">
                        <benefit.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-semibold mb-2">{benefit.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section - Hidden as per request */}
        {/* <section className="w-full py-16 md:py-20 bg-[hsl(var(--surface-1))]">
          ...
        </section> */}
      </main>

      <Footer />
    </>
  );
}
